




























































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { agentsAdminModule } from '@/store/modules/agents-admin-module';

const CONNECTION_EVENT_TYPES = ['agent_connected', 'agent_reconnected', 'agent_disconnected'];
const HEARTBEAT_STALE_MINUTES = 3;

interface DiagnosticResult {
  type: 'success' | 'warning' | 'error' | 'info';
  icon: string;
  title: string;
  message: string;
  hint?: string;
}

@Component
export default class DiagnosticTab extends Vue {
  @Prop({ default: '' }) public agentId!: string;
  @Prop({ default: false }) public loading!: boolean;

  private now: Date = new Date();
  private clockTimer: number | null = null;

  public created() {
    this.clockTimer = window.setInterval(() => { this.now = new Date(); }, 10000);
  }

  public beforeDestroy() {
    if (this.clockTimer !== null) clearInterval(this.clockTimer);
  }

  @Watch('agentId', { immediate: true })
  public async onAgentIdChanged(val: string) {
    if (!val) return;
    try {
      await agentsAdminModule.getAgentState({ agentId: val, take: 100 });
    } catch (_) { /* error ya en store */ }
  }

  public refresh() {
    if (this.agentId) {
      agentsAdminModule.getAgentState({ agentId: this.agentId, take: 100 }).catch(() => {});
    }
  }

  private get fullState(): any {
    return agentsAdminModule.agentFullState || {};
  }

  public get agentInfo(): any {
    return this.fullState.agent || null;
  }

  public get connectionEvents(): any[] {
    const events: any[] = this.fullState.agent_audit_events || [];
    return events
      .filter((e: any) => CONNECTION_EVENT_TYPES.includes(e.event_type))
      .sort((a: any, b: any) => {
        const ra = a.created_at_utc || a.created_at;
        const rb = b.created_at_utc || b.created_at;
        const da = ra ? new Date(ra).getTime() : 0;
        const db = rb ? new Date(rb).getTime() : 0;
        return db - da;
      });
  }

  public get recentHeartbeats(): any[] {
    const hbs: any[] = this.fullState.agent_heartbeats || [];
    return [...hbs]
      .sort((a: any, b: any) => {
        const ra = a.received_at_utc || a.snapshot_at_utc || a.received_at || a.created_at;
        const rb = b.received_at_utc || b.snapshot_at_utc || b.received_at || b.created_at;
        const da = ra ? new Date(ra).getTime() : 0;
        const db = rb ? new Date(rb).getTime() : 0;
        return db - da;
      })
      .slice(0, 5);
  }

  public get recentConnections(): any[] {
    const conns: any[] = this.fullState.agent_connections || [];
    return [...conns]
      .sort((a: any, b: any) => {
        const ra = a.connected_at_utc || a.connected_at;
        const rb = b.connected_at_utc || b.connected_at;
        const da = ra ? new Date(ra).getTime() : 0;
        const db = rb ? new Date(rb).getTime() : 0;
        return db - da;
      })
      .slice(0, 5);
  }

  private get lastEvent(): any | null {
    return this.connectionEvents[0] || null;
  }

  private get lastConnectedEvent(): any | null {
    return this.connectionEvents.find((e: any) =>
      e.event_type === 'agent_connected' || e.event_type === 'agent_reconnected'
    ) || null;
  }

  private get lastDisconnectedEvent(): any | null {
    return this.connectionEvents.find((e: any) => e.event_type === 'agent_disconnected') || null;
  }

  private get lastHeartbeatDate(): Date | null {
    if (!this.recentHeartbeats.length) return null;
    const hb = this.recentHeartbeats[0];
    const raw = hb.received_at_utc || hb.snapshot_at_utc || hb.received_at || hb.created_at;
    return raw ? new Date(raw) : null;
  }

  public get heartbeatStale(): boolean {
    if (!this.lastHeartbeatDate) return true;
    return (this.now.getTime() - this.lastHeartbeatDate.getTime()) > HEARTBEAT_STALE_MINUTES * 60 * 1000;
  }

  private get minutesSinceHeartbeat(): number | null {
    if (!this.lastHeartbeatDate) return null;
    return (this.now.getTime() - this.lastHeartbeatDate.getTime()) / 60000;
  }

  public get statusColor(): string {
    if (!this.lastEvent) return 'grey';
    if (this.lastEvent.event_type === 'agent_disconnected') return 'error';
    return 'success';
  }

  public get statusIcon(): string {
    if (!this.lastEvent) return 'mdi-help-circle';
    if (this.lastEvent.event_type === 'agent_disconnected') return 'mdi-lan-disconnect';
    return 'mdi-lan-connect';
  }

  public get statusLabel(): string {
    if (!this.lastEvent) return 'DESCONOCIDO';
    if (this.lastEvent.event_type === 'agent_disconnected') return 'DESCONECTADO';
    return 'CONECTADO';
  }

  public get lastHeartbeatText(): string {
    if (!this.lastHeartbeatDate) return 'Nunca';
    return `${this.formatDateTime(this.lastHeartbeatDate)} (${this.timeAgo(this.lastHeartbeatDate)})`;
  }

  public get lastConnectedText(): string {
    if (!this.lastConnectedEvent) return 'Sin registro';
    const ts = this.lastConnectedEvent.created_at_utc || this.lastConnectedEvent.created_at;
    return this.formatDateTime(ts);
  }

  public get lastDisconnectedText(): string {
    if (!this.lastDisconnectedEvent) return 'Sin registro';
    const ts = this.lastDisconnectedEvent.created_at_utc || this.lastDisconnectedEvent.created_at;
    return this.formatDateTime(ts);
  }

  public get diagnosticResult(): DiagnosticResult {
    if (!this.connectionEvents.length) {
      return {
        type: 'warning',
        icon: 'mdi-help-circle',
        title: 'Sin historial de conexión',
        message: 'No hay eventos de conexión registrados para este agente.',
        hint: 'El agente puede no haberse conectado nunca, o los datos son anteriores al sistema de auditoría. Verifica la configuración del agente (server_url, hub_path).'
      };
    }

    const last = this.lastEvent;

    if (last.event_type === 'agent_disconnected') {
      const ts = last.created_at_utc || last.created_at;
      const mins = ((this.now.getTime() - new Date(ts).getTime()) / 60000).toFixed(0);
      const isRecent = Number(mins) < 3;
      return {
        type: 'error',
        icon: 'mdi-lan-disconnect',
        title: 'Agente desconectado',
        message: `El agente se desconectó hace ${mins} minuto${Number(mins) === 1 ? '' : 's'}.`,
        hint: isRecent
          ? 'Si el backend acaba de reiniciarse, el agente reconecta automáticamente en ≤90 segundos. Espera y refresca.'
          : 'El agente lleva demasiado tiempo desconectado para ser un reinicio normal. Verifica que la URL del hub sea correcta y que el servicio Windows del agente esté activo.'
      };
    }

    const mins = this.minutesSinceHeartbeat;
    if (mins !== null && mins > HEARTBEAT_STALE_MINUTES) {
      return {
        type: 'warning',
        icon: 'mdi-pulse',
        title: 'Sin heartbeat reciente',
        message: `El agente está registrado pero no envía heartbeat desde hace ${mins.toFixed(0)} minutos.`,
        hint: 'El ciclo de heartbeat del agente puede estar fallando. El agente puede parecer "online" pero no responder a comandos.'
      };
    }

    const secsAgo = mins !== null ? (mins * 60).toFixed(0) : null;
    return {
      type: 'success',
      icon: 'mdi-check-circle',
      title: 'Agente operativo',
      message: `El agente está conectado y enviando heartbeats correctamente.${secsAgo ? ` Último hace ${secsAgo} segundos.` : ''}`,
    };
  }

  public getEventDotColor(eventType: string): string {
    if (eventType === 'agent_connected') return 'success';
    if (eventType === 'agent_reconnected') return 'info';
    if (eventType === 'agent_disconnected') return 'error';
    return 'grey';
  }

  public getEventClass(eventType: string): string {
    if (eventType === 'agent_connected') return 'tl-connected';
    if (eventType === 'agent_reconnected') return 'tl-reconnected';
    if (eventType === 'agent_disconnected') return 'tl-disconnected';
    return '';
  }

  public getEventIcon(eventType: string): string {
    if (eventType === 'agent_connected') return 'mdi-power-plug';
    if (eventType === 'agent_reconnected') return 'mdi-refresh';
    if (eventType === 'agent_disconnected') return 'mdi-power-plug-off';
    return 'mdi-circle-small';
  }

  public formatEventType(eventType: string): string {
    const map: Record<string, string> = {
      agent_connected: 'Conectado',
      agent_reconnected: 'Reconectado',
      agent_disconnected: 'Desconectado'
    };
    return map[eventType] || eventType.replace(/_/g, ' ');
  }

  public formatDateTime(value: any): string {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).format(d);
  }

  public timeAgo(value: any): string {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '—';
    const secs = Math.floor((this.now.getTime() - d.getTime()) / 1000);
    if (secs < 0) return 'ahora';
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}min`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
    return `${Math.floor(secs / 86400)}d`;
  }
}
